import React, { useState, useEffect, useMemo } from 'react';
import { Profile, User } from '../../../types/models';
import { USER_VALIDATIONS, validateUser, getPasswordPolicyDescription } from '../../../utils/validationUtils';
import {
  Autocomplete,
  Box,
  Chip,
  Typography,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  RadioGroup,
  Radio,
  FormControlLabel,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Alert,
  useTheme,
} from '@mui/material';
import PasswordFieldWithSideToggle from '../../../components/common/PasswordFieldWithSideToggle';

export interface UserFormDialogProps {
  open: boolean;
  initialUser: User | null;
  currentUserDepartment: string | undefined;
  /** ログイン中の管理者の所属一覧。undefined = 制限なし（SYSTEM_ADMIN）。空配列 = 会社全体のみ選択可 */
  currentUserDepartments?: string[];
  /** true = profileMgmt 権限あり → 全プロファイル選択可。false = 自分のプロファイルで固定。default = true */
  canSelectProfile?: boolean;
  /** canSelectProfile=false かつ新規作成時に自動設定するプロファイルID */
  callerProfileId?: string;
  /** 操作者の管理範囲（managementScope）。指定された所属チップは削除不可 */
  callerManagementScope?: string;
  profiles?: Profile[];
  departmentOptions?: string[];
  onClose: () => void;
  onSave: (user: User) => Promise<void>;
}

const emptyFormData: User = {
  userId: '',
  givenUserId: '',
  companyId: '',
  email: '',
  displayName: '',
  department: '',
  departments: [],
  managementScope: '',
  profileId: '',
  disable: false,
  userRole: 'USER',
  createdAt: '',
  updatedAt: '',
};

const UserFormDialog: React.FC<UserFormDialogProps> = ({
  open,
  initialUser,
  currentUserDepartment,
  currentUserDepartments,
  canSelectProfile = true,
  callerProfileId,
  callerManagementScope,
  profiles = [],
  departmentOptions = [],
  onClose,
  onSave,
}) => {
  const theme = useTheme();
  const linkBlue = theme.palette.smartSkillTalk?.linkBlue ?? '#1976d2';
  const [formData, setFormData] = useState<User>(emptyFormData);
  const [dialogErrorMessage, setDialogErrorMessage] = useState<string | null>(null);
  useEffect(() => {
    if (!open) return;
    if (initialUser) {
      const depts = initialUser.departments && initialUser.departments.length > 0
        ? initialUser.departments
        : (initialUser.department ? [initialUser.department] : []);
      setFormData({
        userId: initialUser.userId,
        givenUserId: initialUser.givenUserId,
        companyId: initialUser.companyId,
        email: initialUser.email ?? '',
        displayName: initialUser.displayName,
        department: depts[0] ?? '',
        departments: depts,
        managementScope: initialUser.managementScope ?? '',
        profileId: initialUser.profileId ?? '',
        userRole: initialUser.userRole,
        disable: initialUser.disable,
        locked: initialUser.locked,
        tempPassword: initialUser.tempPassword,
        createdAt: initialUser.createdAt,
        updatedAt: initialUser.updatedAt,
      });
    } else {
      const allDepts =
        currentUserDepartments && currentUserDepartments.length > 0
          ? currentUserDepartments
          : currentUserDepartment
            ? [currentUserDepartment]
            : [];
      setFormData({
        ...emptyFormData,
        email: '',
        displayName: '',
        department: allDepts[0] ?? '',
        departments: allDepts,
        managementScope: callerManagementScope ?? '',
        profileId: !canSelectProfile ? (callerProfileId ?? '') : '',
      });
    }
    setDialogErrorMessage(null);
  }, [open, initialUser, currentUserDepartment]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const isAdminRole = formData.userRole === 'ADMIN' || formData.userRole === 'SYSTEM_ADMIN';

  const departments = useMemo(() => formData.departments ?? [], [formData.departments]);

  // departments 候補（入力済み + 既存）
  const deptChoices = useMemo(() => {
    const set = new Set<string>();
    (departmentOptions || []).forEach((d) => d && set.add(d));
    (departments || []).forEach((d) => d && set.add(d));
    if (formData.managementScope) set.add(formData.managementScope);
    return Array.from(set);
  }, [departmentOptions, departments, formData.managementScope]);

  // 管理範囲の選択肢：callerManagementScope が設定されている場合はその値のみ、
  // ブランクの場合は編集中ユーザーに設定済みの所属のみ（取得済み全所属ではない）
  const allowedScopeChoices = useMemo(() => {
    if (callerManagementScope) return [callerManagementScope];
    return Array.from(new Set((departments || []).filter(Boolean)));
  }, [callerManagementScope, departments]);

  // 会社全体（スコープなし）を選択できるか：callerManagementScope がブランク、かつ所属が未設定の場合のみ。
  // 所属が一つでも設定されている管理者は、管理範囲を空欄にできない（所属のいずれかを選択する必要がある）
  const allowCompanyWide = !callerManagementScope && departments.length === 0;

  const isSaveDisabled = () => {
    const validatePasswordField = !initialUser && !!formData.tempPassword;
    const validationResult = validateUser(formData, validatePasswordField);
    if (!validationResult.isValid) return true;
    if (!initialUser && !formData.tempPassword?.trim()) return true;
    if (canSelectProfile && isAdminRole && profiles.length > 0 && !formData.profileId) return true;
    // 管理範囲の制限チェック
    if (isAdminRole) {
      const scope = formData.managementScope || '';
      if (callerManagementScope) {
        // 管理範囲が固定の場合：一致しなければ保存不可
        if (scope !== callerManagementScope) return true;
      }
      // 所属が一つでも設定されている場合、管理範囲のブランクは不可（所属のいずれかを選択する必要がある）
      if (departments.length > 0) {
        if (scope === '' || !departments.includes(scope)) return true;
      }
    }
    return false;
  };

  const handleSave = async () => {
    const validatePasswordField = !initialUser && !!formData.tempPassword;
    const validationResult = validateUser(formData, validatePasswordField);
    if (!validationResult.isValid) {
      setDialogErrorMessage(validationResult.errors.map((e) => e.message).join('\n'));
      return;
    }
    if (!initialUser && !formData.tempPassword?.trim()) {
      setDialogErrorMessage('仮パスワードを入力してください');
      return;
    }
    const depts = formData.departments ?? [];
    const scope = formData.managementScope || '';
    if (isAdminRole && depts.length > 0) {
      if (scope === '') {
        setDialogErrorMessage('所属が設定されている管理者は、管理範囲を空欄にできません。所属のいずれかを管理範囲に設定してください（管理範囲を空欄にする場合は、先に所属を空欄にしてください）');
        return;
      }
      if (!depts.includes(scope)) {
        setDialogErrorMessage(`管理範囲（${scope}）が所属に含まれていません。先に管理範囲を変更してから所属を更新してください`);
        return;
      }
    }
    // USER ロールでは managementScope / profileId はクリア
    const normalized: User = {
      ...formData,
      departments: depts,
      department: depts[0] || '',
      managementScope: isAdminRole ? (formData.managementScope || '') : '',
      profileId: isAdminRole ? (formData.profileId || '') : '',
    };
    setDialogErrorMessage(null);
    try {
      await onSave(normalized);
    } catch (err) {
      setDialogErrorMessage(err instanceof Error ? err.message : 'ユーザーの保存に失敗しました');
    }
  };

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') onClose();
      }}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: '10px' } }}
    >
      <DialogTitle sx={{ bgcolor: linkBlue, color: 'white', fontSize: '18px', fontWeight: 400 }}>
        {initialUser ? 'ユーザー編集' : '新規ユーザー登録'}
      </DialogTitle>
      <DialogContent sx={{ mt: 2 }}>
        {dialogErrorMessage && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDialogErrorMessage(null)}>
            {dialogErrorMessage}
          </Alert>
        )}
        <TextField
          margin="dense"
          id="givenUserId"
          name="givenUserId"
          label="ログインID"
          type="text"
          fullWidth
          variant="outlined"
          value={formData.givenUserId}
          onChange={handleInputChange}
          required={USER_VALIDATIONS.USER_ID.REQUIRED}
          disabled={!!initialUser}
          sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: '3px', '& fieldset': { borderColor: '#898E93' } } }}
        />
        <TextField
          margin="dense"
          id="email"
          name="email"
          label="メールアドレス"
          type="email"
          fullWidth
          variant="outlined"
          value={formData.email ?? ''}
          onChange={handleInputChange}
          required={USER_VALIDATIONS.EMAIL.REQUIRED}
          sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: '3px', '& fieldset': { borderColor: '#898E93' } } }}
        />
        <TextField
          margin="dense"
          id="displayName"
          name="displayName"
          label="氏名"
          type="text"
          fullWidth
          variant="outlined"
          value={formData.displayName}
          onChange={handleInputChange}
          required={USER_VALIDATIONS.DISPLAY_NAME.REQUIRED}
          sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: '3px', '& fieldset': { borderColor: '#898E93' } } }}
        />
        <Autocomplete
          multiple
          freeSolo
          options={deptChoices}
          value={departments}
          onChange={(_, newValue) => {
            let cleaned = Array.from(new Set(newValue.map((v) => String(v).trim()).filter(Boolean)));
            // 操作者の管理範囲所属は削除不可：削除しようとした場合は末尾に復元
            if (callerManagementScope && !cleaned.includes(callerManagementScope)) {
              cleaned = [...cleaned, callerManagementScope];
            }
            setFormData((prev) => {
              const isAdmin = prev.userRole === 'ADMIN' || prev.userRole === 'SYSTEM_ADMIN';
              return {
                ...prev,
                departments: cleaned,
                department: cleaned[0] || '',
                // 管理者かつ所属がブランクになった場合は管理範囲を強制クリア
                managementScope: isAdmin && cleaned.length === 0 ? '' : prev.managementScope,
              };
            });
          }}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => {
              const { key, ...tagProps } = getTagProps({ index });
              const isLocked = !!callerManagementScope && option === callerManagementScope;
              return (
                <Chip
                  key={key}
                  label={option}
                  {...tagProps}
                  onDelete={isLocked ? undefined : tagProps.onDelete}
                />
              );
            })
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="所属（複数可）"
              placeholder="所属を入力"
              helperText="最初に入力した所属が表示に使用されます"
              sx={{ mb: 2 }}
            />
          )}
        />
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
          <Typography variant="subtitle2" sx={{ mr: 2, minWidth: '80px' }}>
            権限:
          </Typography>
          <RadioGroup
            row
            name="userRole"
            value={formData.userRole === 'SYSTEM_ADMIN' ? 'ADMIN' : formData.userRole}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                userRole: e.target.value as 'USER' | 'ADMIN',
                // USER に戻した場合は管理系フィールドをクリア
                // ADMIN に変更した場合、callerManagementScope が設定されていればそれを適用
                managementScope: e.target.value === 'USER' ? '' : (callerManagementScope ?? prev.managementScope),
                profileId: e.target.value === 'USER' ? '' : prev.profileId,
              }))
            }
            sx={{ display: 'flex', justifyContent: 'flex-start' }}
          >
            <FormControlLabel value="USER" control={<Radio />} label="ユーザー" />
            <FormControlLabel value="ADMIN" control={<Radio />} label="管理者" />
          </RadioGroup>
        </Box>

        {isAdminRole && (
          <>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="management-scope-label">管理範囲（空欄＝会社全体）</InputLabel>
              <Select
                labelId="management-scope-label"
                label="管理範囲（空欄＝全体）"
                value={formData.managementScope || ''}
                onChange={(e) => setFormData((prev) => ({ ...prev, managementScope: String(e.target.value) }))}
                disabled={!!callerManagementScope || (isAdminRole && departments.length === 0)}
              >
                {allowCompanyWide && (
                  <MenuItem value="">
                    <em>会社全体（スコープなし）</em>
                  </MenuItem>
                )}
                {allowedScopeChoices.map((d) => (
                  <MenuItem key={d} value={d}>
                    {d}
                  </MenuItem>
                ))}
              </Select>
              {!callerManagementScope && departments.length > 0 && (
                <FormHelperText>
                  所属が設定されている場合、管理範囲は空欄にできません。空欄にするには先に所属を削除してください。
                </FormHelperText>
              )}
            </FormControl>
            {canSelectProfile && profiles.length > 0 && (
              <FormControl fullWidth sx={{ mb: 2 }} required>
                <InputLabel id="profile-label">プロファイル</InputLabel>
                <Select
                  labelId="profile-label"
                  label="プロファイル"
                  value={formData.profileId || ''}
                  onChange={(e) => setFormData((prev) => ({ ...prev, profileId: String(e.target.value) }))}
                >
                  {profiles.map((p) => (
                    <MenuItem key={p.profileId} value={p.profileId}>
                      {p.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </>
        )}

        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
          <Typography variant="subtitle2" sx={{ mr: 2, minWidth: '80px' }}>
            ステータス:
          </Typography>
          <RadioGroup
            row
            name="status"
            value={formData.disable ? '1' : '0'}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, disable: e.target.value === '1' }))
            }
            sx={{ display: 'flex', justifyContent: 'flex-start' }}
          >
            <FormControlLabel value="0" control={<Radio />} label="有効" />
            <FormControlLabel value="1" control={<Radio />} label="無効" />
          </RadioGroup>
        </Box>
        {!initialUser && (
          <Box sx={{ mt: 2 }}>
            <PasswordFieldWithSideToggle
              margin="dense"
              density="dense"
              id="tempPassword"
              name="tempPassword"
              label="仮パスワード"
              fullWidth
              value={formData.tempPassword ?? ''}
              onChange={handleInputChange}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '3px',
                  '& fieldset': { borderColor: '#898E93' },
                },
              }}
              required
            />
            <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block', ml: 1 }}>
              {getPasswordPolicyDescription()}
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={isSaveDisabled()}
          sx={{
            bgcolor: linkBlue,
            color: 'white',
            borderRadius: '6px',
            '&:hover': { bgcolor: linkBlue, opacity: 0.9 },
          }}
        >
          保存
        </Button>
        <Button
          onClick={onClose}
          sx={{
            color: linkBlue,
            borderColor: linkBlue,
            borderRadius: '6px',
            '&:hover': { borderColor: linkBlue, opacity: 0.9 },
          }}
          variant="outlined"
        >
          キャンセル
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserFormDialog;
